import {
  Component, ElementRef, forwardRef, ViewChild, Input
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

@Component({
  selector: 'app-whatsapp-text-editor',
  templateUrl: './whatsapp-text-editor.component.html',
  styleUrls: ['./whatsapp-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WhatsappTextEditorComponent),
      multi: true
    }
  ]
})
export class WhatsappTextEditorComponent implements ControlValueAccessor {
  @ViewChild('editor', { static: true }) editor!: ElementRef<HTMLDivElement>;
  @Input() placeholder = 'Write your message';

  value = '';
  disabled = false;
  currentLength = 0;
  charLimit = 1024;
  showEmojiPicker = false;

  emojis = ['😀', '😂', '😍', '👍', '🙏', '🔥', '🎉', '😢', '😎', '🤔'];

  onChange: (v: string) => void = () => { };
  onTouched: () => void = () => { };

  // CVA: Write value from form → editor
  writeValue(value: string): void {
    this.value = value || '';
    if (this.editor) {
      this.editor.nativeElement.innerText = this.value;
      this.updateCount();
    }
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.editor) {
      this.editor.nativeElement.contentEditable = (!isDisabled).toString();
    }
  }


  // Enforce limit, allow editing but block extra input
  onKeyDown(e: KeyboardEvent) {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Tab'
    ];

    // allow navigation + deletion
    if (allowedKeys.includes(e.key)) return;

    // If reaching limit → block new input
    if (this.currentLength >= this.charLimit && e.key.length === 1) {
      e.preventDefault();
    }
  }

  // Update model when user types
  onInput() {
    let text = this.editor.nativeElement.innerText;

    // Hard enforce limit
    if (text.length > this.charLimit) {
      text = text.slice(0, this.charLimit);
      this.editor.nativeElement.innerText = text;
      this.placeCaretAtEnd();
    }

    this.value = text;
    this.updateCount();
    this.onChange(this.value);
  }

  // Clean paste → plain text only
  onPaste(e: ClipboardEvent) {
    e.preventDefault();
    const text = e.clipboardData?.getData('text/plain') ?? '';
    this.insertAtCaret(text);
    this.onInput();
  }

  // Insert text only if limit allows
  insertAtCaret(text: string) {
    if (this.currentLength + text.length > this.charLimit) {
      text = text.slice(0, this.charLimit - this.currentLength);
    }
    if (!text) return;

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      this.editor.nativeElement.innerText += text;
      this.placeCaretAtEnd();
      return;
    }

    const range = sel.getRangeAt(0);
    range.deleteContents();
    range.insertNode(document.createTextNode(text));
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  // Insert emojis
  pickEmoji(emoji: string) {
    this.insertAtCaret(emoji);
    this.showEmojiPicker = false;
    this.onInput();
  }

  toggleEmoji() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  // Formatting helpers
  format(type: 'bold' | 'italic' | 'strike' | 'mono') {
    const markers: any = {
      bold: '*',
      italic: '_',
      strike: '~',
      mono: '```'
    };
    this.wrapSelection(markers[type]);
    this.onInput();
  }

  wrapSelection(marker: string) {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) {
      this.insertAtCaret(marker + marker);
      this.moveCaret(-marker.length);
      return;
    }

    const range = sel.getRangeAt(0);
    const text = range.toString();

    if (!text) {
      this.insertAtCaret(marker + marker);
      this.moveCaret(-marker.length);
      return;
    }

    const wrapped = marker + text + marker;
    range.deleteContents();
    range.insertNode(document.createTextNode(wrapped));

    this.placeCaretAtEnd();
  }

  // Caret helpers
  placeCaretAtEnd() {
    const el = this.editor.nativeElement;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  moveCaret(offset: number) {
    const text = this.editor.nativeElement.innerText;
    const pos = Math.max(0, text.length + offset);

    const range = document.createRange();
    const walker = document.createTreeWalker(
      this.editor.nativeElement,
      NodeFilter.SHOW_TEXT
    );

    let remaining = pos;
    let found = false;

    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      if (node.length >= remaining) {
        range.setStart(node, remaining);
        range.collapse(true);
        found = true;
        break;
      }
      remaining -= node.length;
    }

    if (!found) {
      range.selectNodeContents(this.editor.nativeElement);
      range.collapse(false);
    }

    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  updateCount() {
    this.currentLength = this.editor.nativeElement.innerText.length;
  }


}
